/**
 * Goal Helpers
 * Utility functions for goal calculations and display
 */

import { GOAL_TYPES } from '../composables/useGoals'

/**
 * Get table headers based on goal type
 * @param {string} goalType - Goal type (כמותי, פיזור, כספי)
 * @returns {Array<string>} Array of header labels
 */
export function getGoalTableHeaders(goalType) {
  switch (goalType) {
    case GOAL_TYPES.QUANTITY:
      return ['מס', 'סוכן', 'יעד', 'ביצוע', 'הפרש']
    case GOAL_TYPES.DISTRIBUTION:
      return ['מס', 'סוכן', 'יעד', 'כמות', 'הפרש']
    case GOAL_TYPES.FINANCIAL:
      return ['מס', 'סוכן', 'יעד', 'סה"כ', 'הפרש']
    default:
      return []
  }
}

/**
 * Calculate the achieved amount for an agent based on goal type
 * @param {Object} agent - Agent object
 * @param {Object} goal - Goal object
 * @returns {number} Calculated amount
 */
export function calculateAgentAmount(agent, goal) {
  const { type, products } = goal

  switch (type) {
    case GOAL_TYPES.QUANTITY:
      // Quantity: divide by box size
      const boxSize = Number(products[0]?.boxSize) || 1
      return agent.amount / boxSize

    case GOAL_TYPES.DISTRIBUTION:
      // Distribution: count of clients
      return agent.agents_clients?.length || 0

    case GOAL_TYPES.FINANCIAL:
      // Financial: direct amount
      return agent.amount || 0

    default:
      return 0
  }
}

/**
 * Format the achieved amount for display
 * @param {Object} agent - Agent object
 * @param {Object} goal - Goal object
 * @returns {string} Formatted amount string
 */
export function formatAgentAmount(agent, goal) {
  const amount = calculateAgentAmount(agent, goal)

  if (goal.type === GOAL_TYPES.DISTRIBUTION) {
    return amount.toString()
  }

  return amount.toFixed(2)
}

/**
 * Calculate the difference between goal and achieved
 * @param {Object} agent - Agent object
 * @param {Object} goal - Goal object
 * @returns {number} Difference (positive means behind goal)
 */
export function calculateAgentDifference(agent, goal) {
  const achieved = calculateAgentAmount(agent, goal)
  return achieved - agent.goal
}

/**
 * Format the difference for display
 * @param {Object} agent - Agent object
 * @param {Object} goal - Goal object
 * @returns {string} Formatted difference string
 */
export function formatAgentDifference(agent, goal) {
  const diff = calculateAgentDifference(agent, goal)

  if (goal.type === GOAL_TYPES.DISTRIBUTION) {
    return diff.toFixed(0)
  }

  if (goal.type === GOAL_TYPES.FINANCIAL) {
    return diff.toFixed(2)
  }

  return diff.toFixed(0)
}

/**
 * Determine if agent is meeting/exceeding goal
 * @param {Object} agent - Agent object
 * @param {Object} goal - Goal object
 * @returns {boolean} True if agent is meeting or exceeding goal
 */
export function isAgentMeetingGoal(agent, goal) {
  return calculateAgentDifference(agent, goal) >= 0
}

/**
 * Calculate goal summary totals
 * @param {Object} goal - Goal object
 * @returns {Object} { totalAchieved, totalGoal, difference }
 */
export function calculateGoalSummary(goal) {
  let totalAchieved = 0
  let totalGoal = 0

  if (goal.agents) {
    goal.agents.forEach((agent) => {
      totalGoal += Number(agent.goal) || 0
      totalAchieved += calculateAgentAmount(agent, goal)
    })
  }

  const difference = totalGoal - totalAchieved

  return {
    totalAchieved,
    totalGoal,
    difference,
  }
}

/**
 * Format goal summary for display
 * @param {Object} goal - Goal object
 * @returns {Object} { achieved: string, goal: string, difference: string, isAhead: boolean }
 */
export function formatGoalSummary(goal) {
  const { totalAchieved, totalGoal, difference } = calculateGoalSummary(goal)

  return {
    achieved: totalAchieved.toFixed(2),
    goal: totalGoal.toString(),
    difference: difference.toFixed(0),
    isAhead: difference <= 0,
  }
}

/**
 * Format a Firestore Timestamp for display
 * @param {Object} timestamp - Firestore Timestamp object
 * @param {string} locale - Locale for formatting (default: 'he')
 * @returns {string} Formatted date string
 */
export function formatTimestamp(timestamp, locale = 'he') {
  if (!timestamp) return ''

  // Handle Firestore Timestamp
  if (timestamp.toDate) {
    return timestamp.toDate().toLocaleDateString(locale)
  }

  // Handle Date object
  if (timestamp instanceof Date) {
    return timestamp.toLocaleDateString(locale)
  }

  // Handle timestamp with seconds property
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000).toLocaleDateString(locale)
  }

  return ''
}

/**
 * Format a Firestore Timestamp for full datetime display
 * @param {Object} timestamp - Firestore Timestamp object
 * @param {string} locale - Locale for formatting (default: 'he')
 * @returns {string} Formatted datetime string
 */
export function formatTimestampFull(timestamp, locale = 'he') {
  if (!timestamp) return 'טרם עודכן'

  // Handle Firestore Timestamp
  if (timestamp.toDate) {
    return timestamp.toDate().toLocaleString(locale)
  }

  // Handle timestamp with seconds property
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000).toLocaleString(locale)
  }

  return 'טרם עודכן'
}

/**
 * Validate goal form data
 * @param {Object} goal - Goal object to validate
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export function validateGoalForm(goal) {
  if (!goal.title || goal.title.length < 3) {
    return { isValid: false, error: 'נא למלא שם ליעד (לפחות 3 תווים)' }
  }

  if (!goal.type) {
    return { isValid: false, error: 'נא לבחור את סוג היעד' }
  }

  if (!goal.makat_type) {
    return { isValid: false, error: 'נא לבחור כיצד לשייך מקטים ליעד' }
  }

  if (!goal.products || goal.products.length === 0) {
    return { isValid: false, error: 'נא לשייך מקטים או מקט מרכז ליעד' }
  }

  if (!goal.date_start) {
    return { isValid: false, error: 'נא לבחור תאריך התחלה ליעד' }
  }

  if (!goal.date_end) {
    return { isValid: false, error: 'נא לבחור תאריך סיום ליעד' }
  }

  const startDate = new Date(goal.date_start)
  const endDate = new Date(goal.date_end)

  if (endDate < startDate) {
    return { isValid: false, error: 'תאריך סיום היעד קטן מתאריך ההתחלה' }
  }

  return { isValid: true, error: null }
}

/**
 * Get goal type badge class/style
 * @param {string} goalType - Goal type
 * @returns {string} CSS class or style
 */
export function getGoalTypeBadgeClass(goalType) {
  switch (goalType) {
    case GOAL_TYPES.QUANTITY:
      return 'goal-type-quantity'
    case GOAL_TYPES.DISTRIBUTION:
      return 'goal-type-distribution'
    case GOAL_TYPES.FINANCIAL:
      return 'goal-type-financial'
    default:
      return ''
  }
}
